import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
// Note: AuthContext login is deprecated - using authService directly
import { VersionInfo } from '../components/Layout/VersionInfo';
import InstallAppBanner from '../components/InstallAppBanner';
import AccountSelectorModal from '../components/AccountSelectorModal';
import PortalSelector, { Portal } from '../components/PortalSelector';
import PasswordInput from '../components/common/PasswordInput';
import { universalLogin, getUserPortals, getAccessToken, getUserData, AccountOption } from '../services/authService';
import { isPlatformAuthenticatorAvailable, authenticateWithPasskey } from '../services/passkeyService';

// Track mount count for debugging
let mountCount = 0;

const Login: React.FC = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [biometricAvailable, setBiometricAvailable] = useState(false);
  const [lockedMinutes, setLockedMinutes] = useState<number | null>(null);
  const [searchParams] = useSearchParams();
  const isMounted = useRef(true);
  const hasCheckedAuth = useRef(false);
  // Multi-account selector state
  const [showAccountSelector, setShowAccountSelector] = useState(false);
  const [availableAccounts, setAvailableAccounts] = useState<AccountOption[]>([]);
  const [pendingCredentials, setPendingCredentials] = useState<{ email: string; password: string } | null>(null);
  // Portal selector state (multi-portal users)
  const [showPortalSelector, setShowPortalSelector] = useState(false);
  const [availablePortals, setAvailablePortals] = useState<Portal[]>([]);
  const [loggedInUserName, setLoggedInUserName] = useState<string>('');

  // Log mount for debugging
  useEffect(() => {
    mountCount++;
    console.log(`[Login] Component MOUNTED (mount #${mountCount}) at:`, new Date().toISOString());
    isMounted.current = true;

    return () => {
      console.log(`[Login] Component UNMOUNTED (was mount #${mountCount})`);
      isMounted.current = false;
    };
  }, []);

  // One-time auth check on mount - verify cookies are still valid before redirecting
  useEffect(() => {
    // Prevent running twice (React StrictMode)
    if (hasCheckedAuth.current) {
      console.log('[Login] Auth check already ran, skipping');
      setShowForm(true);
      return;
    }
    hasCheckedAuth.current = true;

    console.log('[Login] Running auth check...');

    // Clean up legacy localStorage tokens from pre-httpOnly migration
    localStorage.removeItem('rbf_access_token');
    localStorage.removeItem('rbf_refresh_token');
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');

    // CRITICAL FIX: Don't trust localStorage alone — verify with the server.
    // Old flow checked localStorage and redirected immediately, causing a loop
    // when cookies were expired but localStorage still had stale user data.
    // New flow: call /api/auth/me/ to verify, show form on ANY failure.
    const verifyAndRedirect = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL || 'http://localhost:8000/api'}/auth/me/`,
          { credentials: 'include', headers: { 'Accept': 'application/json' } }
        );

        if (!response.ok) throw new Error('Not authenticated');

        const userData = await response.json();
        if (!userData || !userData.id) throw new Error('Invalid user data');
        if (!userData.is_staff && !userData.is_superuser) throw new Error('Not staff');

        // Authenticated — determine where to redirect
        const portals = userData.portals || [];
        const isSuperuser = userData.is_superuser;
        const returnUrl = searchParams.get('return') || searchParams.get('next');
        let redirectTo = '/dashboard';

        if (returnUrl) {
          redirectTo = returnUrl;
        } else if (!isSuperuser && !portals.includes('admin') && portals.includes('delivery')) {
          redirectTo = '/delivery';
        } else if (isSuperuser || portals.includes('admin') || portals.includes('office')) {
          redirectTo = '/dashboard';
        } else if (portals.includes('mill')) {
          redirectTo = '/mill/dashboard';
        } else if (portals.includes('store')) {
          redirectTo = '/store/dashboard';
        }

        console.log('[Login] Verified authenticated, redirecting to:', redirectTo);
        window.location.replace(redirectTo);
        return; // Don't show form — we're redirecting
      } catch (e) {
        // Auth failed (401, timeout, network error, not staff) — clear stale data, show form
        console.log('[Login] Auth verification failed, showing login form:', (e as Error).message);
        localStorage.removeItem('rbf_user_data');
        localStorage.removeItem('user');
      }

      // Not authenticated — show form
      if (isMounted.current) {
        setShowForm(true);
      }
    };

    // Safety valve: if auth check takes >5s, show login form regardless
    const safetyTimer = setTimeout(() => {
      if (isMounted.current && !showForm) {
        console.log('[Login] Auth check timeout, showing form');
        localStorage.removeItem('rbf_user_data');
        localStorage.removeItem('user');
        setShowForm(true);
      }
    }, 5000);

    verifyAndRedirect().finally(() => clearTimeout(safetyTimer));
  }, []); // Empty deps - only run once

  useEffect(() => {
    if (showForm) {
      checkBiometric();
    }
  }, [showForm]);

  const checkBiometric = async () => {
    const available = await isPlatformAuthenticatorAvailable();
    setBiometricAvailable(available);
  };

  const redirectAfterLogin = () => {
    // Check for redirect URL (supports both 'return' and 'next' parameters)
    const returnUrl = searchParams.get('return') || searchParams.get('next');
    if (returnUrl) {
      window.location.href = returnUrl;
      return;
    }

    // Redirect to highest-priority portal user can access
    // Use window.location.href to force full page reload with new auth state
    const portals = getUserPortals();
    if (portals.includes('admin')) {
      window.location.href = '/dashboard';
    } else if (portals.includes('delivery')) {
      window.location.href = '/delivery';
    } else if (portals.includes('office')) {
      window.location.href = '/dashboard';
    } else if (portals.includes('mill')) {
      window.location.href = '/mill/dashboard';
    } else if (portals.includes('store')) {
      window.location.href = '/store/dashboard';
    } else {
      window.location.href = '/dashboard';
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    setLockedMinutes(null);

    try {
      // Use universal login - works for any user type
      console.log('[Login] Attempting universal login for:', username);
      const result = await universalLogin(username, password);
      console.log('[Login] Result:', result);

      if (result.success) {
        // Check if multiple accounts found - show selector
        if (result.auto_login === false && result.accounts && result.accounts.length > 1) {
          console.log('[Login] Multiple accounts found, showing selector');
          setAvailableAccounts(result.accounts);
          setPendingCredentials({ email: username, password });
          setShowAccountSelector(true);
          setLoading(false);
          return;
        }

        // Auth tokens are stored in httpOnly cookies (not accessible to JS)
        console.log('[Login] SUCCESS - user type:', result.user_type);

        // Check if we should show portal selector (user has multiple portals)
        const portals = result.user?.available_portals;
        if (result.show_portal_selector && portals && portals.length > 1) {
          console.log('[Login] Multiple portals available, showing selector');
          setAvailablePortals(portals);
          setLoggedInUserName(result.user?.name?.split(' ')[0] || '');
          setShowPortalSelector(true);
          setLoading(false);
          return;
        }

        // Check for redirect URL in query params first
        const returnUrl = searchParams.get('return') || searchParams.get('next');

        if (returnUrl) {
          // Honor explicit redirect request
          window.location.href = returnUrl;
        } else if (result.redirect_url) {
          // Use the redirect URL from the universal login response
          window.location.href = result.redirect_url;
        } else {
          // Fallback to default redirect based on user type
          if (result.user_type === 'team') {
            redirectAfterLogin();
          } else if (result.user_type === 'portal') {
            window.location.href = '/portal/dashboard';
          } else if (result.user_type === 'bridge_club') {
            window.location.href = '/bridge-club/dashboard';
          }
        }
      } else {
        setError(result.error || 'Invalid email or password');
        if (result.locked_minutes) {
          setLockedMinutes(result.locked_minutes);
        }
      }
    } catch (err: unknown) {
      console.error('[Login] Error:', err);
      if (err instanceof Error) {
        setError(err.message || 'Invalid email or password');
      } else {
        setError('Invalid email or password');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAccountSelect = async (accountType: 'team' | 'portal' | 'bridge_club') => {
    if (!pendingCredentials) return;

    setLoading(true);
    setError('');

    try {
      console.log('[Login] Completing login for account type:', accountType);
      const result = await universalLogin(pendingCredentials.email, pendingCredentials.password, accountType);

      if (result.success && result.auto_login) {
        console.log('[Login] Account selection complete, redirecting to:', result.redirect_url);
        setShowAccountSelector(false);

        const returnUrl = searchParams.get('return') || searchParams.get('next');
        if (returnUrl) {
          window.location.href = returnUrl;
        } else if (result.redirect_url) {
          window.location.href = result.redirect_url;
        }
      } else {
        setError(result.error || 'Login failed');
      }
    } catch (err) {
      console.error('[Login] Account selection error:', err);
      setError('Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleAccountSelectorCancel = () => {
    setShowAccountSelector(false);
    setPendingCredentials(null);
    setAvailableAccounts([]);
  };

  const handlePortalSelect = (portal: Portal) => {
    console.log('[Login] Portal selected:', portal.id, portal.url);
    setShowPortalSelector(false);
    window.location.href = portal.url;
  };

  const handleBiometricLogin = async () => {
    setLoading(true);
    setError('');
    setLockedMinutes(null);

    const result = await authenticateWithPasskey(username || undefined);

    if (result.success) {
      redirectAfterLogin();
    } else {
      setError(result.error || 'Biometric login failed');
    }

    setLoading(false);
  };

  // Show loading while checking auth state
  if (!showForm) {
    return (
      <div className="login-page" style={styles.container}>
        <div style={styles.loginCard}>
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <p>Checking authentication...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-page" style={styles.container}>
      <div style={styles.loginCard}>
        <div style={styles.header}>
          <img src="/rbf-logo.png" alt="Red Bridge Farm Feed Co." style={styles.logo} />
          <h1 style={styles.title}>Team Login</h1>
          <p style={styles.subtitle}>Red Bridge Farm Feed Co.</p>
          <p style={styles.tagline}>NON-GMO, SOY & CORN-FREE FEEDS</p>
        </div>

        {/* Biometric Login Button */}
        {biometricAvailable && (
          <div style={styles.biometricSection}>
            <button
              type="button"
              style={styles.biometricButton}
              onClick={handleBiometricLogin}
              disabled={loading}
            >
              <span style={styles.biometricIcon}>👆</span>
              Sign in with Face ID / Touch ID
            </button>

            <div style={styles.divider}>
              <span style={styles.dividerLine}></span>
              <span style={styles.dividerText}>or use password</span>
              <span style={styles.dividerLine}></span>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} style={styles.form}>
          {error && (
            <div style={styles.error}>
              <p style={{ margin: 0 }}>{error}</p>
              {lockedMinutes && (
                <p style={{ margin: '8px 0 0', fontWeight: 600 }}>
                  Try again in {lockedMinutes} minutes
                </p>
              )}
            </div>
          )}

          <div style={styles.inputGroup}>
            <label style={styles.label}>Email or Username</label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              style={styles.input}
              placeholder="Enter your email or username"
              required
              disabled={loading}
              autoComplete="email"
            />
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label}>Password</label>
            <PasswordInput
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={styles.input}
              placeholder="Enter your password"
              required
              disabled={loading}
              autoComplete="current-password"
            />
          </div>

          <button type="submit" style={styles.submitButton} disabled={loading}>
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>

        <div style={styles.footer}>
          <p style={styles.footerText}>Staff access only. One login for Admin, Mill, and Store portals.</p>
          <p style={styles.customerLink}>
            Wholesale Customer? <a href="/portal/login" style={styles.link}>Customer Portal →</a>
          </p>
          <p style={styles.bridgeClubLink}>
            Bridge Club Member? <a href="/bridge-club/login" style={styles.bridgeLink}>Bridge Club →</a>
          </p>
          <VersionInfo />
        </div>
      </div>
      <InstallAppBanner />

      {/* Account Selector Modal */}
      {showAccountSelector && availableAccounts.length > 0 && (
        <AccountSelectorModal
          accounts={availableAccounts}
          onSelect={handleAccountSelect}
          onCancel={handleAccountSelectorCancel}
          loading={loading}
        />
      )}

      {/* Portal Selector Modal (multi-portal users) */}
      {showPortalSelector && availablePortals.length > 1 && (
        <PortalSelector
          portals={availablePortals}
          userName={loggedInUserName}
          onSelect={handlePortalSelect}
        />
      )}
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #1a4d1a 0%, #2e7d32 50%, #1b5e20 100%)',
    padding: '2rem',
  },
  loginCard: {
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
    padding: '3rem',
    width: '100%',
    maxWidth: '420px',
  },
  header: {
    textAlign: 'center',
    marginBottom: '2rem',
  },
  logo: {
    height: '80px',
    marginBottom: '1rem',
  },
  title: {
    margin: 0,
    fontSize: '1.8rem',
    fontWeight: 700,
    color: '#2D5A27',
    textTransform: 'uppercase',
    letterSpacing: '2px',
  },
  subtitle: {
    margin: '0.5rem 0 0',
    fontSize: '0.95rem',
    color: '#666',
  },
  tagline: {
    margin: '0.75rem 0 0',
    fontSize: '0.75rem',
    color: '#1a4d1a',
    fontWeight: 600,
    letterSpacing: '1.5px',
  },
  biometricSection: {
    marginBottom: '1.5rem',
  },
  biometricButton: {
    width: '100%',
    padding: '1rem',
    fontSize: '1rem',
    fontWeight: 600,
    background: 'linear-gradient(135deg, #2D5A27 0%, #4A7C42 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '10px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    transition: 'opacity 0.2s',
  },
  biometricIcon: {
    fontSize: '1.5rem',
  },
  divider: {
    display: 'flex',
    alignItems: 'center',
    margin: '1.5rem 0',
  },
  dividerLine: {
    flex: 1,
    height: '1px',
    backgroundColor: '#e0e0e0',
  },
  dividerText: {
    padding: '0 1rem',
    color: '#888',
    fontSize: '0.9rem',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1.25rem',
  },
  error: {
    backgroundColor: '#fef2f2',
    border: '1px solid #fecaca',
    color: '#dc2626',
    padding: '0.75rem 1rem',
    borderRadius: '8px',
    fontSize: '0.9rem',
  },
  inputGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
  },
  label: {
    fontSize: '0.9rem',
    fontWeight: 600,
    color: '#333',
  },
  input: {
    padding: '0.875rem 1rem',
    fontSize: '1rem',
    border: '2px solid #e0e0e0',
    borderRadius: '8px',
    outline: 'none',
    backgroundColor: '#f8f9fa',
    transition: 'border-color 0.2s, box-shadow 0.2s',
  },
  submitButton: {
    background: '#333',
    color: '#ffffff',
    border: 'none',
    padding: '1rem',
    fontSize: '1rem',
    fontWeight: 600,
    borderRadius: '8px',
    cursor: 'pointer',
    marginTop: '0.5rem',
    transition: 'background 0.2s',
  },
  footer: {
    marginTop: '2rem',
    textAlign: 'center',
    borderTop: '1px solid #e0e0e0',
    paddingTop: '1.5rem',
  },
  footerText: {
    margin: 0,
    fontSize: '0.85rem',
    color: '#888',
  },
  customerLink: {
    margin: '0.75rem 0 0.5rem',
    fontSize: '0.9rem',
    color: '#666',
  },
  bridgeClubLink: {
    margin: '0 0 0.75rem',
    fontSize: '0.9rem',
    color: '#666',
  },
  link: {
    color: '#2D5A27',
    textDecoration: 'none',
    fontWeight: 600,
  },
  bridgeLink: {
    color: '#E73C3E',
    textDecoration: 'none',
    fontWeight: 600,
  },
};

export default Login;
